import type { CognitiveIndices } from './eegIndices';
import type { HandTraits } from './handIndices';

/**
 * Trait-score → percent normalisation.
 *
 * Raw indices have no canonical scale, and the EEG ratios are heavily
 * skewed (clean Fatigue spans ~0.1–15 at p5–p95), so a linear
 * `[min, max]` mapping can't represent them: an earlier 0.2–8 range put
 * every real session in the top fifth of the bar. Instead each metric
 * is mapped to its **percentile among reference windows** — 50% is a
 * typical second, 90% is higher than nine in ten.
 *
 * Every bar reads as the *level of the thing it names*: a high Stress /
 * Fatigue / Tremor bar means MORE stress / fatigue / tremor. (They used
 * to be inverted to "100% = good", which made a calm surgeon's 95%
 * Fatigue bar read as exhausted.) {@link ScoringRule.highIsGood} records
 * which direction is desirable, for copy and colour hints only.
 *
 * Reference: ACS Conference 2026 (staging tenant `acs`), the largest
 * recording of each of 46 participants, Muse Gen 2 + MetaWear, ticked
 * once a second exactly as the live screen / replay do. EEG quantiles
 * use only seconds with at least one clean channel (see
 * eegIndices.ts `isCleanWindow`) — 32,899 seconds from 39 sessions;
 * hand quantiles 47,206 seconds from 46 sessions.
 *
 * To recalibrate after a study: regenerate `quantiles` (21 knots, every
 * 5%, from p0 to p100). The display components consume these via
 * {@link rawToPercent} and never hard-code thresholds.
 */

export type TraitKey =
  | 'focus'
  | 'stress'
  | 'fatigue'
  | 'tremor'
  | 'smoothness'
  | 'steady';

export interface ScoringRule {
  /**
   * Raw values at p0, p5, … p100 of the reference set (21 knots,
   * ascending). A raw value maps to its interpolated percentile.
   */
  quantiles: readonly number[];
  /** Whether a high level is the desirable direction (Focus, Steady,
   *  Smoothness) or not (Stress, Fatigue, Tremor). Not used in scoring. */
  highIsGood: boolean;
  label: string;
  /** Bar fill colour, picked to match the reference app's metric palette. */
  color: string;
}

const RULES: Record<TraitKey, ScoringRule> = {
  focus: {
    // β / α
    quantiles: [
      0.0478, 0.628, 0.939, 1.23, 1.49, 1.78, 2.09, 2.41, 2.76, 3.16, 3.6,
      4.1, 4.71, 5.44, 6.38, 7.55, 9.37, 12.2, 17.0, 28.0, 983.0,
    ],
    highIsGood: true,
    label: 'Focus', color: '#26a69a',
  },
  stress: {
    // β_high / α
    quantiles: [
      0.00754, 0.219, 0.361, 0.501, 0.645, 0.794, 0.96, 1.16, 1.36, 1.6,
      1.87, 2.18, 2.56, 3.04, 3.67, 4.56, 5.81, 7.85, 11.6, 20.6, 775.0,
    ],
    highIsGood: false,
    label: 'Stress', color: '#ffa726',
  },
  fatigue: {
    // (α + θ) / β
    quantiles: [
      0.00815, 0.149, 0.256, 0.362, 0.476, 0.592, 0.713, 0.851, 0.991, 1.16,
      1.37, 1.61, 1.92, 2.33, 2.85, 3.49, 4.41, 5.81, 8.26, 14.7, 198.0,
    ],
    highIsGood: false,
    label: 'Fatigue', color: '#9c66e0',
  },
  tremor: {
    // fraction of motion power in 4–12 Hz
    quantiles: [
      0.0113, 0.1712, 0.2267, 0.2642, 0.2941, 0.3192, 0.3407, 0.3599,
      0.3784, 0.3962, 0.4136, 0.4316, 0.4498, 0.4694, 0.4898, 0.5134, 0.54,
      0.571, 0.6099, 0.6661, 0.9373,
    ],
    highIsGood: false,
    label: 'Tremor', color: '#ef5350',
  },
  smoothness: {
    quantiles: [
      0.3348, 0.4285, 0.4364, 0.4494, 0.4654, 0.4768, 0.4865, 0.4951,
      0.5037, 0.5121, 0.5206, 0.5299, 0.5397, 0.5503, 0.562, 0.575, 0.5899,
      0.6085, 0.6325, 0.6705, 0.8942,
    ],
    highIsGood: true,
    label: 'Smoothness', color: '#66bb6a',
  },
  steady: {
    // Asymptotes near 1.0 for a still hand, so the top knots crowd.
    quantiles: [
      0.2775, 0.8623, 0.9073, 0.9285, 0.9415, 0.9504, 0.9569, 0.9623,
      0.9668, 0.9706, 0.9738, 0.9768, 0.9795, 0.9819, 0.9844, 0.9869,
      0.9898, 0.994, 0.9988, 0.9989, 0.9992,
    ],
    highIsGood: true,
    label: 'Steady', color: '#4c8dff',
  },
};

/** All metrics in display order (matches reference app's 3×2 grid). */
export const TRAIT_KEYS: TraitKey[] = [
  'focus',
  'stress',
  'steady',
  'tremor',
  'fatigue',
  'smoothness',
];

export function getRule(metric: TraitKey): ScoringRule {
  return RULES[metric];
}

/**
 * Map one raw index value to its 0-100 percentile against the reference
 * set, interpolating linearly between the 5% knots. Returns `null` for
 * non-finite inputs so the UI can render '—' instead of a fake bar.
 */
export function rawToPercent(metric: TraitKey, raw: number): number | null {
  if (!Number.isFinite(raw)) return null;
  const q = RULES[metric].quantiles;
  const last = q.length - 1;
  if (raw <= q[0]) return 0;
  if (raw >= q[last]) return 100;
  let i = 0;
  while (raw > q[i + 1]) i++;
  const span = q[i + 1] - q[i];
  const frac = span > 0 ? (raw - q[i]) / span : 0;
  return Math.round(((i + frac) / last) * 100);
}

/**
 * Helper: average the per-hand values for a single hand-metric across
 * the hands that have data. Returns `null` if neither hand has a
 * value. Lets the headline % treat both hands as one operator score.
 */
function avgPerHand(
  perHand: Record<string, HandTraits>,
  key: 'tremor' | 'smoothness' | 'steady',
): number | null {
  const values: number[] = [];
  for (const t of Object.values(perHand)) {
    if (Number.isFinite(t[key])) values.push(t[key]);
  }
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Pull one metric's raw value (averaged across hands where relevant)
 * from a live tick of indices. Returns `null` when the metric isn't
 * computable (e.g. EEG not yet warm, or no hand data).
 */
export function traitRaw(
  metric: TraitKey,
  cognitive: CognitiveIndices | null,
  hand: Record<string, HandTraits>,
): number | null {
  switch (metric) {
    case 'focus':
      return cognitive ? cognitive.focus : null;
    case 'stress':
      return cognitive ? cognitive.stress : null;
    case 'fatigue':
      return cognitive ? cognitive.fatigue : null;
    case 'tremor':
      return avgPerHand(hand, 'tremor');
    case 'smoothness':
      return avgPerHand(hand, 'smoothness');
    case 'steady':
      return avgPerHand(hand, 'steady');
  }
}

/**
 * Same as {@link traitRaw} but for a finalised per-phase summary
 * pair — used to render the Done-screen comparison bars.
 */
export function traitRawForSummary(
  metric: TraitKey,
  cognitiveAvg: {
    focus: number | null;
    stress: number | null;
    fatigue: number | null;
  } | null,
  handAvg: Record<
    string,
    { tremor: number; smoothness: number; steady: number }
  >,
): number | null {
  switch (metric) {
    case 'focus':
      return cognitiveAvg?.focus ?? null;
    case 'stress':
      return cognitiveAvg?.stress ?? null;
    case 'fatigue':
      return cognitiveAvg?.fatigue ?? null;
    case 'tremor':
    case 'smoothness':
    case 'steady': {
      const vals: number[] = [];
      for (const entry of Object.values(handAvg)) {
        const v = entry[metric];
        if (Number.isFinite(v)) vals.push(v);
      }
      if (vals.length === 0) return null;
      return vals.reduce((a, b) => a + b, 0) / vals.length;
    }
  }
}
